import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  // Add accordion group functionality
  get #accordionGroup() {
    return this.dataset.accordionGroup || null;
  }

  get #enableSingleOpen() {
    return this.dataset.singleOpen === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('toggle', this.#handleToggle, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Handles the toggle event to implement accordion behavior
   * @param {Event} event - The toggle event
   */
  #handleToggle = (event) => {
    // Only proceed if this accordion is opening and single-open is enabled
    if (this.details.open && this.#enableSingleOpen) {
      this.#closeOtherAccordions();
    }
  };

  /**
   * Closes other accordions in the same group or all accordions with single-open
   */
  #closeOtherAccordions() {
    let selector = 'accordion-custom[data-single-open="true"]';
    
    // If accordion group is specified, only close within that group
    if (this.#accordionGroup) {
      selector = `accordion-custom[data-accordion-group="${this.#accordionGroup}"]`;
    }

    const accordions = document.querySelectorAll(selector);
    
    accordions.forEach(accordion => {
      // Don't close the current accordion
      if (accordion !== this && accordion instanceof AccordionCustom) {
        accordion.details.open = false;
      }
    });
  }

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    // Check if this should be open by default
    const shouldBeOpen = 
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));

    // If single-open is enabled, handle the first accordion logic
    if (this.#enableSingleOpen && shouldBeOpen) {
      const accordions = document.querySelectorAll('accordion-custom[data-single-open="true"]');
      const firstAccordion = accordions[0];
      
      // Only open this accordion if it's the first one, or if no other accordion is currently open
      const hasOpenAccordion = Array.from(accordions).some(acc => 
        acc !== this && acc instanceof AccordionCustom && acc.details.open
      );
      
      if (this === firstAccordion || !hasOpenAccordion) {
        this.details.open = true;
        // Close other accordions if they're open
        this.#closeOtherAccordions();
      } else {
        this.details.open = false;
      }
    } else if (!this.#enableSingleOpen) {
      // Normal behavior for non-single-open accordions
      this.details.open = shouldBeOpen;
    }
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
